import { useCallback, useState } from "react";
import type { IDetectedBarcode } from "@yudiel/react-qr-scanner";
import { Camera, CheckCircle, Loader2 } from "lucide-react";
import QRCodeReader from "../../../../components/qr-code-reader";
import { getApiErrorMessage } from "../../../../lib/result-codes";
import { useRedeemCouponByQr } from "../hooks/useRedeemCouponByQr";
import type { CouponRedeemResult, RedeemErrorKind } from "../types/merchantCouponRedeemer.types";
import {
  extractTokenFromQRCode,
  formatRedeemedAt,
  getRedeemErrorKind,
  redeemErrorTitles,
} from "../utils/merchantCouponRedeemer.utils";

type CameraPermission = "idle" | "granted" | "denied";
type RedeemStatus = "idle" | "loading" | "success" | "error";

const MerchantCouponRedeemerPage = () => {
  const { execute, isLoading } = useRedeemCouponByQr();
  const [cameraPermission, setCameraPermission] = useState<CameraPermission>("idle");
  const [showScanner, setShowScanner] = useState(false);
  const [paused, setPaused] = useState(false);
  const [redeemStatus, setRedeemStatus] = useState<RedeemStatus>("idle");
  const [redeemData, setRedeemData] = useState<CouponRedeemResult | null>(null);
  const [redeemErrorKind, setRedeemErrorKind] = useState<RedeemErrorKind>("generic");
  const [redeemError, setRedeemError] = useState<string | null>(null);

  const requestCameraAccess = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((track) => track.stop());
      setCameraPermission("granted");
      setShowScanner(true);
    } catch {
      setCameraPermission("denied");
    }
  };

  const handleScan = useCallback(
    async (codes: IDetectedBarcode[]) => {
      const firstCode = codes[0];
      if (!firstCode || isLoading || redeemStatus === "loading") return;
      setPaused(true);
      setRedeemStatus("loading");
      setRedeemError(null);
      setRedeemData(null);
      try {
        const result = await execute(extractTokenFromQRCode(firstCode.rawValue));
        if (result === undefined) {
          setPaused(false);
          setRedeemStatus("idle");
          return;
        }
        setRedeemData(result);
        setRedeemStatus("success");
      } catch (requestError: unknown) {
        setRedeemErrorKind(getRedeemErrorKind(requestError));
        setRedeemError(
          requestError instanceof Error
            ? requestError.message
            : getApiErrorMessage(requestError, "Failed to redeem coupon. Please try again."),
        );
        setRedeemStatus("error");
      }
    },
    [execute, isLoading, redeemStatus],
  );

  const handleRescan = () => {
    setRedeemStatus("idle");
    setRedeemData(null);
    setRedeemError(null);
    setRedeemErrorKind("generic");
    setPaused(false);
  };

  if (!showScanner) {
    const isDenied = cameraPermission === "denied";
    return (
      <div className="flex flex-col min-h-full">
        <div className="flex-1 flex items-center justify-center pt-12">
          <div
            className={`w-24 h-24 rounded-full flex items-center justify-center ${isDenied ? "bg-red-100 dark:bg-red-900/20" : "bg-[#E8F8F5] dark:bg-emerald-900/30"}`}
          >
            <Camera
              size={48}
              className={
                isDenied ? "text-red-400 dark:text-red-300" : "text-[#00C49A] dark:text-emerald-400"
              }
            />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-t-3xl px-6 py-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white text-center pb-4">
            {isDenied ? "Camera Access Denied" : "Enable Camera Access"}
          </h2>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400 text-center leading-relaxed">
            {isDenied
              ? "Please enable camera access in your browser settings to scan coupon QR codes."
              : "To redeem coupons, we need access to your device's camera. Your camera will only be used for scanning resident coupon QR codes."}
          </p>
          <button
            onClick={() => void requestCameraAccess()}
            className="mt-6 w-full h-12 bg-[#004232] dark:bg-emerald-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2"
          >
            <Camera size={20} />
            <span>{isDenied ? "Try Again" : "Enable Camera"}</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-fit bg-[#1E293B] flex-1">
      <div className="pt-6 pb-8">
        <h2 className="text-lg font-medium text-white text-center">Scan coupon QR code</h2>
      </div>
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="relative w-[280px] h-[280px] border-[3px] border-white rounded-lg">
          <div className="absolute inset-2 overflow-hidden rounded-lg">
            <QRCodeReader
              paused={paused}
              onScan={handleScan}
            />
          </div>
        </div>
      </div>
      {redeemStatus === "loading" && (
        <div className="p-6 bg-white dark:bg-gray-800">
          <div className="flex items-center justify-center gap-3">
            <Loader2
              size={24}
              className="text-[#00C49A] animate-spin"
            />
            <span className="text-gray-600 dark:text-gray-300 font-medium">
              Redeeming coupon...
            </span>
          </div>
        </div>
      )}
      {redeemStatus === "success" && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle
              size={20}
              className="text-green-600 dark:text-green-400"
            />
            <span className="font-semibold text-green-800 dark:text-green-300">
              Coupon Redeemed!
            </span>
          </div>
          {redeemData && (
            <div className="mt-2 space-y-1">
              <p className="text-sm text-green-700 dark:text-green-300 font-medium">
                {redeemData.couponTitle}
              </p>
              {redeemData.redeemedAt && (
                <p className="text-sm text-green-600 dark:text-green-400">
                  {formatRedeemedAt(redeemData.redeemedAt)}
                </p>
              )}
            </div>
          )}
          <button
            onClick={handleRescan}
            className="mt-4 w-full py-2.5 bg-[#00C49A] dark:bg-emerald-500 text-white rounded-lg font-medium"
          >
            Scan Next
          </button>
        </div>
      )}
      {redeemStatus === "error" && (
        <div className="flex flex-col items-center px-6 pb-8 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-full px-4 py-3 shadow-sm">
            <p className="text-sm text-gray-800 dark:text-gray-200 text-center">
              {redeemErrorTitles[redeemErrorKind]}
            </p>
            {redeemError && (
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                {redeemError}
              </p>
            )}
          </div>
          <button
            onClick={handleRescan}
            className="px-6 py-3 bg-[#00C49A] dark:bg-emerald-500 text-white rounded-full font-medium"
          >
            Continue Scanning
          </button>
        </div>
      )}
    </div>
  );
};

export default MerchantCouponRedeemerPage;
